import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';
import {
  ArrowLeft,
  User,
  Building,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Download
} from 'lucide-react';

interface CustomerDetail {
  id: string;
  sw_customer_id: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  account_status: string | null;
  balance: number | null;
  credit_limit: number | null;
  preferred_contact: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  sw_companies: {
    name: string;
    id: string;
  } | null;
  [key: string]: any; // For any additional fields
}

const CustomerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchField, setSearchField] = useState('');

  useEffect(() => {
    if (id) {
      fetchCustomer();
    }
  }, [id]);

  const fetchCustomer = async () => {
    if (!supabase || !id) return;

    try {
      setLoading(true);
      setError(null);

      // Query customer by service_account_id instead of id
      const { data, error: fetchError } = await supabase
        .from('sw_customers')
        .select('*')
        .eq('service_account_id', id)
        .single();

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      // Add company name based on company_id
      if (data) {
        const companyName = getCompanyName(data.company_id);
        setCustomer({
          ...data,
          sw_companies: { name: companyName, id: data.company_id }
        });
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching customer:', err);
    } finally {
      setLoading(false);
    }
  };

  const getCompanyName = (companyId: string | null): string => {
    if (!companyId) return 'Right Away Home Warranty';
    if (companyId === 'hales-ac') return 'Hales AC';
    if (companyId === 'climate-control') return 'Climate Control Services';
    if (companyId === 'abraham-ac') return 'Abraham AC';
    return 'Unknown';
  };

  const formatCustomerName = (customer: CustomerDetail) => {
    if (customer.company_name) {
      return customer.company_name;
    }
    if (customer.first_name || customer.last_name) {
      return `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    }
    return 'Unnamed Customer';
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const getFieldDisplayName = (fieldName: string) => {
    const fieldMap: Record<string, string> = {
      // Basic fields
      sw_customer_id: 'SW Customer ID',
      sw_service_account_id: 'SW Service Account ID',
      first_name: 'First Name',
      last_name: 'Last Name',
      company_name: 'Company Name',
      email: 'Email',

      // Phone fields
      phone: 'Primary Phone',
      phone_extension: 'Phone Extension',
      phone2: 'Phone 2',
      phone2_extension: 'Phone 2 Extension',
      phone3: 'Phone 3',
      phone3_extension: 'Phone 3 Extension',
      phone4: 'Phone 4',
      phone4_extension: 'Phone 4 Extension',

      // Customer details
      title: 'Title',
      alt_first_name: 'Alternate First Name',
      alt_last_name: 'Alternate Last Name',
      alternate_title: 'Alternate Title',
      referral: 'Referral Source',
      commercial: 'Commercial Customer',
      no_email: 'No Email on File',

      // Lead tracking
      source_type: 'Source Type',
      lead_source: 'Lead Source',
      lead_source_type_id: 'Lead Source Type ID',
      lead_source_id: 'Lead Source ID',

      // Address fields
      address: 'Address',
      address2: 'Address Line 2',
      city: 'City',
      state: 'State',
      zip: 'ZIP Code',
      location_type: 'Location Type',
      location_notes: 'Location Notes',
      location_number: 'Location Number',
      owner_occupied: 'Owner Occupied',
      tax_exempt: 'Tax Exempt',
      zone: 'Service Zone',

      // Financial fields
      billing_customer_id: 'Billing Customer ID',
      credit_limit: 'Credit Limit',
      balance_due: 'Balance Due',
      current_balance: 'Current Balance',
      over_30_balance: '30+ Days Balance',
      over_60_balance: '60+ Days Balance',
      over_90_balance: '90+ Days Balance',
      total_invoice_amount: 'Total Invoiced',
      total_paid_amount: 'Total Paid',
      retained_balance: 'Retained Balance',

      // Payment info
      payment_method: 'Payment Method',
      payment_terms: 'Payment Terms',
      credit_card_name: 'Credit Card Name',
      credit_card_expire_month: 'CC Expire Month',
      credit_card_expire_year: 'CC Expire Year',

      // Account flags
      account_status: 'Account Status',
      bad_risk_customer: 'Bad Risk Customer',
      do_not_solicit: 'Do Not Solicit',
      happy_checks: 'Happy Checks Enabled',
      finance_charge: 'Finance Charge',
      no_statement: 'No Statement',

      // Sales and contact
      sales_person_id: 'Sales Person ID',
      customer_rating: 'Customer Rating',
      ap_contact: 'AP Contact',
      service_contact: 'Service Contact',
      comments: 'Comments',

      // System tracking
      sw_created_by: 'SW Created By',
      sw_created_at: 'SW Created At',
      last_age_date: 'Last Age Date',
      last_finance_date: 'Last Finance Date',
      created_at: 'Created At',
      updated_at: 'Updated At',

      // Complex data
      service_locations_data: 'Service Locations',
      billing_locations_data: 'Billing Locations',
      raw_data: 'Raw API Data'
    };
    return fieldMap[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const shouldShowField = (key: string, value: any) => {
    // Skip these fields from the detailed view
    const skipFields = ['id', 'company_id', 'sw_companies'];
    if (skipFields.includes(key)) return false;

    // Skip if searching and field doesn't match
    if (searchField && !key.toLowerCase().includes(searchField.toLowerCase()) &&
        !getFieldDisplayName(key).toLowerCase().includes(searchField.toLowerCase())) {
      return false;
    }

    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: theme.colors.bgPage, color: theme.colors.textPrimary }}>
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: theme.colors.primaryColor }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: theme.colors.bgPage, color: theme.colors.textPrimary }}>
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex items-center gap-4 mb-8">
            <Link to="/customers" className="flex items-center gap-2" style={{ color: theme.colors.primaryColor }}>
              <ArrowLeft className="h-4 w-4" />
              Back to Customers
            </Link>
          </div>
          <div className="rounded-lg p-6" style={{ backgroundColor: theme.colors.dangerBg, border: `1px solid ${theme.colors.dangerBorder}` }}>
            <p style={{ color: theme.colors.dangerText }}>Error: {error || 'Customer not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const allFields = Object.entries(customer).filter(([key, value]) => shouldShowField(key, value));

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.colors.bgPage, color: theme.colors.textPrimary }}>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/customers" className="flex items-center gap-2" style={{ color: theme.colors.primaryColor }}>
            <ArrowLeft className="h-4 w-4" />
            Back to Customers
          </Link>
          <div className="h-6 w-px" style={{ backgroundColor: theme.colors.borderColor }}></div>
          <h1 className="text-3xl font-bold" style={{ color: theme.colors.textPrimary }}>{formatCustomerName(customer)}</h1>
        </div>

        {/* Customer Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="rounded-lg p-6" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
            <div className="flex items-center">
              <User className="h-8 w-8 mr-3" style={{ color: theme.colors.primaryColor }} />
              <div>
                <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Customer ID</p>
                <p className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>{customer.sw_customer_id}</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg p-6" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
            <div className="flex items-center">
              <Building className="h-8 w-8 mr-3" style={{ color: theme.colors.successText }} />
              <div>
                <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Company</p>
                <p className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>{customer.sw_companies?.name || 'Unknown'}</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg p-6" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
            <div className="flex items-center">
              <Mail className="h-8 w-8 mr-3" style={{ color: theme.colors.infoText }} />
              <div>
                <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Status</p>
                <p className="text-xl font-bold capitalize" style={{ color: theme.colors.textPrimary }}>{customer.account_status || 'Unknown'}</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg p-6" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
            <div className="flex items-center">
              <Calendar className="h-8 w-8 mr-3" style={{ color: theme.colors.warningText }} />
              <div>
                <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Customer Since</p>
                <p className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>{new Date(customer.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Contact Info */}
        <div className="rounded-lg p-6 mb-8" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
          <h2 className="text-xl font-semibold mb-4" style={{ color: theme.colors.textPrimary }}>Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {customer.email && (
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5" style={{ color: theme.colors.primaryColor }} />
                <div>
                  <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Email</p>
                  <a href={`mailto:${customer.email}`} style={{ color: theme.colors.primaryColor }}>
                    {customer.email}
                  </a>
                </div>
              </div>
            )}

            {customer.phone && (
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5" style={{ color: theme.colors.successText }} />
                <div>
                  <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Phone</p>
                  <a href={`tel:${customer.phone}`} style={{ color: theme.colors.successText }}>
                    {customer.phone}
                  </a>
                </div>
              </div>
            )}

            {(customer.address || customer.city || customer.state || customer.zip) && (
              <div className="flex items-start gap-3 md:col-span-2">
                <MapPin className="h-5 w-5 mt-1" style={{ color: theme.colors.dangerText }} />
                <div>
                  <p className="text-sm" style={{ color: theme.colors.textSecondary }}>Address</p>
                  <div style={{ color: theme.colors.textPrimary }}>
                    {customer.address && <p>{customer.address}</p>}
                    <p>{customer.city}{customer.state && `, ${customer.state}`} {customer.zip}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Search Fields */}
        <div className="rounded-lg p-6 mb-6" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4" style={{ color: theme.colors.textTertiary }} />
              <input
                type="text"
                placeholder="Search customer fields..."
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: theme.colors.bgPage,
                  border: `1px solid ${theme.colors.borderColor}`,
                  color: theme.colors.textPrimary
                }}
              />
            </div>
            <div className="text-sm" style={{ color: theme.colors.textSecondary }}>
              Showing {allFields.length} fields
            </div>
          </div>
        </div>

        {/* All Customer Data */}
        <div className="rounded-lg overflow-hidden" style={{ backgroundColor: theme.colors.bgCard, border: `1px solid ${theme.colors.borderColor}` }}>
          <div className="p-6 border-b" style={{ borderColor: theme.colors.borderColor }}>
            <h2 className="text-xl font-semibold" style={{ color: theme.colors.textPrimary }}>Complete Customer Data</h2>
          </div>

          <div style={{ borderTop: `1px solid ${theme.colors.borderColor}` }}>
            {allFields.map(([key, value]) => (
              <div 
                key={key} 
                className="p-4 transition-colors" 
                style={{ borderBottom: `1px solid ${theme.colors.borderColor}` }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>{getFieldDisplayName(key)}</p>
                    <p className="text-xs font-mono" style={{ color: theme.colors.textTertiary }}>{key}</p>
                  </div>
                  <div className="md:col-span-2">
                    <div style={{ color: theme.colors.textPrimary }}>
                      {key.includes('_at') && value ? (
                        <span>{new Date(value).toLocaleString()}</span>
                      ) : (
                        <span style={{ color: value === null || value === undefined ? theme.colors.textTertiary : theme.colors.textPrimary, fontStyle: value === null || value === undefined ? 'italic' : 'normal' }}>
                          {formatValue(value)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex gap-3">
          <button 
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-opacity"
            style={{ backgroundColor: theme.colors.primaryColor }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            <Edit className="h-4 w-4" />
            Edit Customer
          </button>
          <button 
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
            style={{ border: `1px solid ${theme.colors.borderColor}`, color: theme.colors.textPrimary }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <Download className="h-4 w-4" />
            Export Data
          </button>
          <button 
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
            style={{ border: `1px solid ${theme.colors.dangerBorder}`, color: theme.colors.dangerText }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.dangerBg}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <Trash2 className="h-4 w-4" />
            Delete Customer
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailPage;
